import { useEffect, useRef } from "react";
import { toast } from "sonner";

// ---------- Perimeter Shield wrapper — dynamic border + safe→unverified toast ----------
export function PerimeterShieldWrapper({ unverified, flaggedCount, firstFlaggedId, navigate, children }) {
  const prevRef = useRef(null);
  const lastToastAtRef = useRef(0);
  useEffect(() => {
    // Fire a one-time amber toast on any safe → unverified transition, but rate-limit
    // to at most one alert every 5 minutes so an oscillating poll never spams the user.
    if (prevRef.current === false && unverified === true) {
      const now = Date.now();
      if (now - lastToastAtRef.current > 5 * 60 * 1000) {
        lastToastAtRef.current = now;
        toast.warning(
          `Shield alert · ${flaggedCount} receipt${flaggedCount === 1 ? "" : "s"} flagged for review`,
          {
            description: "Round-number or uncategorized entries detected. Tap to review.",
            duration: 8000,
            action: firstFlaggedId
              ? {
                  label: "Review flagged",
                  onClick: () => navigate(`/receipts/${firstFlaggedId}`),
                }
              : {
                  label: "Open receipts",
                  onClick: () => navigate("/receipts"),
                },
          }
        );
      }
    }
    // Recovery toast — closing the compliance loop when the shield settles back to safe.
    if (prevRef.current === true && unverified === false) {
      toast.success("Shield restored · all clear", {
        description: "Every recent receipt is categorized and within pattern bounds.",
        duration: 4000,
        id: "shield-recovery",
      });
    }
    prevRef.current = unverified;
  }, [unverified, flaggedCount, firstFlaggedId, navigate]);

  const shieldColor = unverified ? "#F59E0B" : "#10B981";
  const shieldTestState = unverified ? "unverified" : "safe";
  return (
    <div
      data-testid="perimeter-shield"
      data-shield-state={shieldTestState}
      className={`perimeter-shield rounded-2xl lg:col-span-2 ${unverified ? "perimeter-shield--unverified" : "perimeter-shield--safe"}`}
      style={{
        borderColor: shieldColor,
        "--shield-color": shieldColor,
        "--shield-color-soft": unverified ? "rgba(245,158,11,0.5)" : "rgba(16,185,129,0.55)",
      }}
    >
      {children}
      <style>{`
        .perimeter-shield {
          border-width: 1px;
          border-style: solid;
          transition: border-color 400ms ease-out, box-shadow 400ms ease-out;
          position: relative;
        }
        .perimeter-shield--safe {
          box-shadow: 0 0 0 1px var(--shield-color-soft),
                      0 0 22px -4px var(--shield-color-soft);
          animation: shield-safe-breathe 3.6s ease-in-out infinite;
        }
        @keyframes shield-safe-breathe {
          0%, 100% {
            box-shadow: 0 0 0 1px rgba(16,185,129,0.35),
                        0 0 22px -6px rgba(16,185,129,0.35);
          }
          50% {
            box-shadow: 0 0 0 1px rgba(16,185,129,0.6),
                        0 0 36px -4px rgba(16,185,129,0.5);
          }
        }
        .perimeter-shield--unverified {
          animation: shield-amber-chase 2.4s linear infinite;
        }
        @keyframes shield-amber-chase {
          0%   { box-shadow: 0 0 0 1px rgba(245,158,11,0.35),  0 -14px 22px -6px rgba(245,158,11,0.6); }
          25%  { box-shadow: 0 0 0 1px rgba(245,158,11,0.55),  14px 0 22px -6px rgba(245,158,11,0.7); }
          50%  { box-shadow: 0 0 0 1px rgba(245,158,11,0.4),   0 14px 22px -6px rgba(245,158,11,0.6); }
          75%  { box-shadow: 0 0 0 1px rgba(245,158,11,0.55), -14px 0 22px -6px rgba(245,158,11,0.7); }
          100% { box-shadow: 0 0 0 1px rgba(245,158,11,0.35),  0 -14px 22px -6px rgba(245,158,11,0.6); }
        }
      `}</style>
    </div>
  );
}


